import { conversationStudy } from '../context';
import { issueHead } from './logic';
import type { ReportCheck, ReportIssue } from './shapes';
import { COMMON_TEXT, el, loading, type RadiologistDeps, toolCard, toolFailure, traceNodes } from './ui';

/**
 * "Revisar informe" (contract v3 §2.2): ris reads the reader's own draft, runs its rule checks
 * (units, critical not communicated, accepted findings missing) and, when the model is available,
 * the model's review. The card lists the issues, most severe first, with the section and the excerpt
 * they are about. It never edits the draft.
 */

export const CHECK_TEXT = {
  tool: 'Revisar informe',
  title: 'Revisión del borrador',
  checking: 'revisando el borrador…',
  disclaimer: 'Revisión sugerida antes de firmar · no edita el borrador · no firma',
  none: 'No se encontraron observaciones en el borrador.',
  failed: 'No fue posible revisar el borrador.',
  count: (n: number) => (n === 1 ? '1 observación' : `${n} observaciones`),
} as const;

function renderIssue(issue: ReportIssue): HTMLElement {
  const item = el('li', `assistant-issue issue-${issue.severity}`);
  item.dataset.severity = issue.severity;
  item.dataset.kind = issue.kind;
  item.appendChild(el('div', 'assistant-issue-head', issueHead(issue)));
  if (issue.excerpt) item.appendChild(el('blockquote', 'assistant-issue-excerpt', issue.excerpt));
  item.appendChild(el('div', 'assistant-issue-message', issue.message));
  return item;
}

export function renderCheck(check: ReportCheck): HTMLElement[] {
  const out: HTMLElement[] = [el('p', 'assistant-tool-note', CHECK_TEXT.disclaimer)];
  if (check.issues.length === 0) {
    out.push(el('p', 'assistant-empty assistant-issues-none', CHECK_TEXT.none));
  } else {
    out.push(el('div', 'assistant-issues-count', CHECK_TEXT.count(check.issues.length)));
    const list = el('ul', 'assistant-issues');
    check.issues.forEach((issue) => list.appendChild(renderIssue(issue)));
    out.push(list);
  }
  out.push(...traceNodes(check));
  if (!check.model) out.push(el('div', 'assistant-trace', COMMON_TEXT.rulesOnly));
  return out;
}

export async function showReportCheck(deps: RadiologistDeps): Promise<void> {
  const study = conversationStudy(deps.shell);
  const { body } = toolCard(deps, 'report-check', CHECK_TEXT.title, study);
  loading(body, CHECK_TEXT.checking);
  try {
    body.replaceChildren(...renderCheck(await deps.api.reportCheck(study)));
  } catch (error) {
    console.warn('[viewer] report check failed', error);
    body.replaceChildren(el('p', 'assistant-tool-note assistant-tool-error', toolFailure(error, 'report_check', CHECK_TEXT.failed)));
  }
}
